/**
 * OperatingModesTimeline — a left-to-right "event structure" view of an
 * equipment's declared FSM (`data.entity.operating_modes`): the states as a
 * process-ordered sequence of segments, each transition between them showing
 * WHAT TRIGGERS IT (the predicate + signal) and WHAT HAPPENS AFTER (the next
 * state). This is the read-only, timeline companion of {@link OperatingModesCard}'s
 * textual transition list — the same model, seen as a sequence.
 *
 * The main strip is the canonical walk from the declared current/initial mode,
 * taking one forward transition per state (preferring progress through the
 * declared mode order) until it loops back or dead-ends. Branch / loop
 * transitions that are NOT on that walk are listed below as "Other transitions"
 * so the full structure stays visible — the walk is the happy path, not a claim
 * that it is the only behaviour. No wall-clock is implied: the axis is process
 * sequence, since real dwell timing is derived at runtime by the predicate
 * runtime, not authored here.
 */

import { type CSSProperties } from "react";
import { ArrowRight, RotateCcw } from "lucide-react";

import type { EntityDef } from "@foundry/foundry-plant";
import type { ModeTransition, ModeTrigger, OperatingModes } from "@/lib/plant/operating-modes";
import { MODE_HUES } from "@/lib/plant/operating-modes-viz";
import { Badge } from "@/lib/ui/badge";

function modeStyle(index: number): CSSProperties {
  const h = MODE_HUES[index % MODE_HUES.length];
  return {
    backgroundColor: `hsl(${h} 48% 50% / 0.12)`,
    borderColor: `hsl(${h} 45% 50% / 0.55)`,
  };
}

/** A compact one-line description of what fires a transition ("what triggers it"). */
function triggerSummary(trigger?: ModeTrigger): string {
  const p = trigger?.predicate;
  if (!p) return "manual / derived event";
  const sig = trigger?.tag_hint ?? "signal";
  switch (p) {
    case "analog_cross_above":
    case "analog_cross_below": {
      const op = p === "analog_cross_above" ? "≥" : "≤"; // >= / <=
      const lvl = trigger?.level !== undefined ? ` ${trigger.level}` : "";
      const db = trigger?.deadband !== undefined ? ` ±${trigger.deadband}` : "";
      const dwell = trigger?.min_dwell_ms ?? trigger?.dwell_ms;
      const hold = dwell !== undefined ? ` for ${dwell}ms` : "";
      return `${sig} ${op}${lvl}${db}${hold}`;
    }
    case "coil_rising":
      return `${sig} turns on`;
    case "coil_falling":
      return `${sig} turns off`;
    case "coil_equals":
      return `${sig} = ${String(trigger?.value ?? "")}`;
    case "count_pulse":
      return `${sig} pulse`;
    case "count_delta":
      return `${sig} increments`;
    case "elapsed_since": {
      const secs = trigger?.duration_ms !== undefined ? trigger.duration_ms / 1000 : undefined;
      const after = trigger?.after_event ? ` after ${trigger.after_event}` : "";
      return secs !== undefined ? `${secs}s elapsed${after}` : `time elapsed${after}`;
    }
    case "operator_push":
      return "operator action";
    case "instrument_push":
      return "instrument reading";
    default:
      return p;
  }
}

interface WalkStep {
  from: string;
  transition: ModeTransition;
  to: string;
  loops: boolean;
}

interface Walk {
  initial: string | undefined;
  bands: string[];
  forward: WalkStep[];
  loops: WalkStep[];
  others: ModeTransition[];
}

/**
 * Greedy canonical walk of the FSM from the declared start: one outgoing
 * transition per state, preferring forward progress through the declared mode
 * order, until it revisits a state (loop) or dead-ends. Everything not consumed
 * is returned as `others` so no declared behaviour is hidden.
 */
function buildWalk(om: OperatingModes): Walk {
  const modes = om.modes ?? [];
  const transitions = om.transitions ?? [];
  const initial = om.current_mode && modes.includes(om.current_mode) ? om.current_mode : modes[0];
  const indexOf = (m: string) => modes.indexOf(m);

  const used = new Set<ModeTransition>();
  const seen = new Set<string>();
  const forward: WalkStep[] = [];
  const loops: WalkStep[] = [];

  let cur = initial;
  while (cur && !seen.has(cur)) {
    seen.add(cur);
    const outgoing = transitions.filter(
      (t) => !used.has(t) && ((t.from ?? []).length === 0 || (t.from ?? []).includes(cur as string)),
    );
    if (outgoing.length === 0) break;

    const unseen = outgoing.filter((t) => !seen.has(t.to));
    const forwardUnseen = unseen
      .filter((t) => indexOf(t.to) > indexOf(cur as string))
      .sort((a, b) => indexOf(a.to) - indexOf(b.to));
    const pick =
      forwardUnseen[0] ?? unseen[0] ?? outgoing.find((t) => seen.has(t.to));
    if (!pick) break;

    used.add(pick);
    const step: WalkStep = { from: cur, transition: pick, to: pick.to, loops: seen.has(pick.to) };
    if (step.loops) {
      loops.push(step);
      break;
    }
    forward.push(step);
    cur = pick.to;
  }

  const bands = initial ? [initial, ...forward.map((s) => s.to)] : [];
  const others = transitions.filter((t) => !used.has(t));
  return { initial, bands, forward, loops, others };
}

/** One state segment: a coloured band labelled with the mode name. */
function ModeSegment({ mode, index, isStart }: { mode: string; index: number; isStart: boolean }) {
  return (
    <div
      className="flex min-w-[7rem] shrink-0 flex-col justify-center rounded-md border border-l-4 bg-surface-muted/40 px-2.5 py-2"
      style={modeStyle(index)}
      title={isStart ? "declared start mode" : undefined}
    >
      <span className="font-mono text-xs font-semibold text-on-surface">{mode}</span>
      {isStart && <span className="text-[10px] uppercase tracking-wide text-on-surface-muted">start</span>}
    </div>
  );
}

/** The connector between two segments: the event that fires + a compact trigger summary. */
function StepConnector({ step }: { step: WalkStep }) {
  const summary = triggerSummary(step.transition.trigger);
  return (
    <div
      className="flex shrink-0 flex-col items-center justify-center gap-0.5 px-1"
      title={`on ${step.transition.on_event}: ${summary}`}
    >
      <Badge variant="outline" className="max-w-[11rem] truncate px-1.5 py-0 font-mono text-[11px]">
        {step.transition.on_event}
      </Badge>
      <ArrowRight className="size-4 text-on-surface-muted" aria-hidden />
      <span className="max-w-[11rem] truncate text-center text-[10px] text-on-surface-muted">
        {summary}
      </span>
    </div>
  );
}

/** A non-path transition (a branch, or a loop back), rendered as one compact row. */
function OtherTransitionRow({ t, loop }: { t: ModeTransition; loop?: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
      {loop && <RotateCcw className="size-3 text-on-surface-muted" aria-hidden />}
      <span className="font-mono text-on-surface-muted">{(t.from ?? []).join(" | ") || "*"}</span>
      <ArrowRight className="size-3 text-on-surface-muted" aria-hidden />
      <span className="font-mono font-semibold text-on-surface">{t.to}</span>
      <span className="text-on-surface-muted">on</span>
      <Badge variant="outline" className="px-1.5 py-0 font-mono text-[11px]">
        {t.on_event}
      </Badge>
      <span className="text-on-surface-muted">{triggerSummary(t.trigger)}</span>
    </div>
  );
}

export interface OperatingModesTimelineProps {
  entity: EntityDef;
}

/**
 * The event-structure timeline for an entity's declared FSM: states left to
 * right, each step showing what triggers it and what follows. Renders nothing
 * when the entity declares no modes/transitions (the caller decides placement).
 */
export function OperatingModesTimeline({ entity }: OperatingModesTimelineProps) {
  const om = entity.operating_modes;
  if (!om || (om.modes?.length ?? 0) === 0 || (om.transitions?.length ?? 0) === 0) return null;

  const { bands, forward, loops, others } = buildWalk(om);
  if (bands.length === 0) return null;

  const bandIndex = (mode: string) => om.modes.indexOf(mode);

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-on-surface-muted">Event timeline</span>

      {/* The canonical walk, left to right. Scrolls within its own track so a long chain never widens the page. */}
      <div className="overflow-x-auto pb-1">
        <div className="flex w-max items-stretch gap-1">
          {bands.map((mode, i) => (
            <div key={`${mode}-${i}`} className="flex items-stretch gap-1">
              <ModeSegment mode={mode} index={bandIndex(mode)} isStart={i === 0} />
              {i < forward.length && <StepConnector step={forward[i]} />}
            </div>
          ))}
        </div>
      </div>

      {(loops.length > 0 || others.length > 0) && (
        <div className="flex flex-col gap-1 rounded-md border border-border bg-surface-muted/30 px-2.5 py-1.5">
          <span className="text-[11px] font-medium text-on-surface-muted">
            Other transitions
            <span className="ml-1 font-normal">(branches, loops, off-path)</span>
          </span>
          {loops.map((s, i) => (
            <OtherTransitionRow key={`loop-${i}`} t={s.transition} loop />
          ))}
          {others.map((t, i) => (
            <OtherTransitionRow key={`other-${t.on_event}-${t.to}-${i}`} t={t} />
          ))}
        </div>
      )}

      <p className="text-[10px] text-on-surface-muted">
        States in process sequence; timing is derived at runtime, not to scale.
      </p>
    </div>
  );
}
